import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Breadcrumbs,
  useTheme,
  useMediaQuery,
  alpha,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  PersonAdd as PersonAddIcon,
  People as PeopleIcon,
  Archive as ArchiveIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Gavel as BrandIcon,
  SupervisorAccount as SupervisorAccountIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-toastify';
import { can } from '../../utils/permissions';
import { ROLE_LABEL } from '../../types/staff';

const SIDEBAR_WIDTH = 240;
const RAIL_WIDTH = 64;
const TOPBAR_HEIGHT = 56;

const NAVY = '#1E3A5F';
const ACCENT = '#3B82F6';

type Crumb = { label: string; path?: string };

const Layout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { currentUser, logout, role } = useAuth();

  const isCompact = useMediaQuery('(max-width:1024px)');
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', visible: true },
    { text: 'Clients', icon: <PeopleIcon />, path: '/clients', visible: can(role, 'viewClients') },
    { text: 'Add Client', icon: <PersonAddIcon />, path: '/add-client', visible: can(role, 'addClient') },
    { text: 'Archived', icon: <ArchiveIcon />, path: '/archived-clients', visible: can(role, 'viewArchived') },
    { text: 'Staff', icon: <SupervisorAccountIcon />, path: '/staff', visible: can(role, 'manageStaff') },
  ].filter((item) => item.visible);

  const buildCrumbs = (): Crumb[] => {
    const path = location.pathname;
    if (path === '/') return [{ label: 'Dashboard' }];
    if (path === '/clients') return [{ label: 'Dashboard', path: '/' }, { label: 'Clients' }];
    if (path === '/add-client')
      return [
        { label: 'Dashboard', path: '/' },
        { label: 'Clients', path: '/clients' },
        { label: 'Add Client' },
      ];
    if (path === '/archived-clients')
      return [{ label: 'Dashboard', path: '/' }, { label: 'Archived' }];
    if (path === '/staff')
      return [{ label: 'Dashboard', path: '/' }, { label: 'Staff' }];
    if (path.startsWith('/client/'))
      return [
        { label: 'Dashboard', path: '/' },
        { label: 'Clients', path: '/clients' },
        { label: 'Profile' },
      ];
    return [{ label: 'Dashboard' }];
  };

  const userInitials = (() => {
    const name = currentUser?.displayName?.trim();
    const email = currentUser?.email ?? '';
    if (name) {
      const parts = name.split(/\s+/).filter(Boolean);
      const a = parts[0]?.[0] ?? '';
      const b = parts[1]?.[0] ?? '';
      const initials = (a + b).toUpperCase();
      return initials || (name[0]?.toUpperCase() ?? 'U');
    }
    return email.slice(0, 2).toUpperCase() || 'U';
  })();

  const userDisplayName =
    currentUser?.displayName ||
    currentUser?.email?.split('@')[0] ||
    'User';

  const handleLogout = async () => {
    setUserMenuAnchor(null);
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch {
      toast.error('Failed to logout');
    }
  };

  // Sidebar width logic:
  //   xs: hidden (use temporary drawer instead)
  //   sm-lg (compact): rail (64) by default; expanded (240) when mobileOpen toggled
  //   xl+ (≥1025): permanent expanded (240)
  const desktopSidebarWidth = isCompact ? (mobileOpen ? SIDEBAR_WIDTH : RAIL_WIDTH) : SIDEBAR_WIDTH;
  const sidebarCollapsed = isCompact && !mobileOpen;

  const renderNavItem = (
    item: typeof menuItems[number],
    collapsed: boolean,
  ) => {
    const active =
      location.pathname === item.path ||
      (item.path !== '/' && location.pathname.startsWith(item.path));

    const button = (
      <ListItemButton
        onClick={() => {
          navigate(item.path);
          if (isXs) setMobileOpen(false);
        }}
        sx={{
          position: 'relative',
          minHeight: 40,
          borderRadius: 2,
          px: collapsed ? 1.5 : 2,
          justifyContent: collapsed ? 'center' : 'flex-start',
          color: active ? '#FFFFFF' : alpha('#FFFFFF', 0.72),
          backgroundColor: active ? alpha('#FFFFFF', 0.1) : 'transparent',
          '&:hover': {
            backgroundColor: alpha('#FFFFFF', active ? 0.14 : 0.06),
            color: '#FFFFFF',
          },
          '&::before': active
            ? {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 8,
                bottom: 8,
                width: 3,
                borderRadius: 1,
                backgroundColor: ACCENT,
              }
            : {},
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 32,
            mr: collapsed ? 0 : 1.5,
            color: 'inherit',
            justifyContent: 'center',
            '& svg': { fontSize: 20 },
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 500,
              letterSpacing: 0,
            }}
          />
        )}
      </ListItemButton>
    );

    return collapsed ? (
      <Tooltip key={item.text} title={item.text} placement="right" arrow>
        <Box>{button}</Box>
      </Tooltip>
    ) : (
      <Box key={item.text}>{button}</Box>
    );
  };

  const sidebarContent = (collapsed: boolean) => (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: NAVY,
        color: '#FFFFFF',
      }}
    >
      <Box
        sx={{
          height: TOPBAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: collapsed ? 0 : 2.5,
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderBottom: `1px solid ${alpha('#FFFFFF', 0.08)}`,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 2,
            bgcolor: ACCENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <BrandIcon sx={{ fontSize: 18, color: '#FFFFFF' }} />
        </Box>
        {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.95rem',
                fontWeight: 700,
                lineHeight: 1.2,
                color: '#FFFFFF',
              }}
            >
              Huduma
            </Typography>
            <Typography
              sx={{
                fontSize: '0.7rem',
                color: alpha('#FFFFFF', 0.6),
                lineHeight: 1.2,
              }}
            >
              Client Records
            </Typography>
          </Box>
        )}
      </Box>
      <List
        sx={{
          flex: 1,
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
        }}
      >
        {menuItems.map((item) => renderNavItem(item, collapsed))}
      </List>
    </Box>
  );

  const crumbs = buildCrumbs();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Sidebar nav (desktop / tablet rail) */}
      <Box
        component="nav"
        sx={{
          width: { xs: 0, sm: desktopSidebarWidth },
          flexShrink: 0,
          transition: 'width 200ms ease',
        }}
      >
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: desktopSidebarWidth,
              boxSizing: 'border-box',
              borderRight: 'none',
              transition: 'width 200ms ease',
              overflowX: 'hidden',
            },
          }}
        >
          {sidebarContent(sidebarCollapsed)}
        </Drawer>
        <Drawer
          variant="temporary"
          open={mobileOpen && isXs}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              boxSizing: 'border-box',
              borderRight: 'none',
            },
          }}
        >
          {sidebarContent(false)}
        </Drawer>
      </Box>

      {/* Top bar */}
      <AppBar
        position="fixed"
        sx={{
          ml: { sm: `${desktopSidebarWidth}px` },
          width: { xs: '100%', sm: `calc(100% - ${desktopSidebarWidth}px)` },
          height: TOPBAR_HEIGHT,
          backgroundColor: '#FFFFFF',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          transition: 'width 200ms ease, margin-left 200ms ease',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            minHeight: `${TOPBAR_HEIGHT}px !important`,
            px: { xs: 2, sm: 3 },
            gap: 1,
          }}
        >
          <IconButton
            aria-label="Toggle navigation"
            edge="start"
            onClick={() => setMobileOpen((s) => !s)}
            sx={{ color: 'text.secondary' }}
            size="small"
          >
            <MenuIcon />
          </IconButton>

          <Breadcrumbs
            separator="/"
            aria-label="breadcrumb"
            sx={{
              flex: 1,
              ml: 1,
              minWidth: 0,
              '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
              '& .MuiBreadcrumbs-li': { minWidth: 0 },
              '& .MuiBreadcrumbs-separator': { color: 'text.disabled', mx: 1 },
            }}
          >
            {crumbs.map((crumb, i, arr) => {
              const isLast = i === arr.length - 1;
              const onlyLastOnXs = i < arr.length - 1;
              return isLast ? (
                <Typography
                  key={i}
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Box
                  key={i}
                  component="button"
                  onClick={() => crumb.path && navigate(crumb.path)}
                  sx={{
                    display: { xs: onlyLastOnXs ? 'none' : 'inline', sm: 'inline' },
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    p: 0,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    fontFamily: 'inherit',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: 'text.primary',
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {crumb.label}
                </Box>
              );
            })}
          </Breadcrumbs>

          {/* User identity */}
          <Box
            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setUserMenuAnchor(e.currentTarget as HTMLElement);
              }
            }}
            role="button"
            aria-label="Open user menu"
            aria-haspopup="menu"
            tabIndex={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              borderRadius: 2,
              px: 1,
              py: 0.5,
              transition: 'background-color 150ms ease',
              '&:hover': { bgcolor: 'action.hover' },
              '&:focus-visible': {
                outline: `2px solid ${ACCENT}`,
                outlineOffset: 2,
              },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: NAVY,
                color: '#FFFFFF',
                fontSize: '0.8rem',
                fontWeight: 600,
              }}
            >
              {userInitials}
            </Avatar>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                minWidth: 0,
                maxWidth: 180,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.primary',
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {userDisplayName}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.7rem',
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {currentUser?.email}
              </Typography>
            </Box>
            <ArrowDownIcon
              sx={{
                fontSize: 18,
                color: 'text.secondary',
                display: { xs: 'none', sm: 'block' },
              }}
            />
          </Box>

          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            TransitionComponent={Fade}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1,
                minWidth: 220,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 10px 25px rgba(0,0,0,0.08)',
                borderRadius: 2,
              },
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'text.primary',
                  lineHeight: 1.3,
                }}
              >
                {userDisplayName}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {currentUser?.email}
              </Typography>
              {role && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    mt: 0.75,
                    px: 1,
                    py: 0.25,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    color: '#1D4ED8',
                    bgcolor: '#EFF6FF',
                    borderRadius: 999,
                  }}
                >
                  {ROLE_LABEL[role]}
                </Box>
              )}
            </Box>
            <MenuItem
              onClick={() => {
                setUserMenuAnchor(null);
                toast.info('Profile page coming soon');
              }}
              sx={{ fontSize: '0.875rem', gap: 1.5, py: 1.25 }}
            >
              <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              My Profile
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={handleLogout}
              sx={{
                fontSize: '0.875rem',
                gap: 1.5,
                py: 1.25,
                color: 'error.main',
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          mt: `${TOPBAR_HEIGHT}px`,
          width: {
            xs: '100%',
            sm: `calc(100% - ${desktopSidebarWidth}px)`,
          },
          transition: 'width 200ms ease',
        }}
      >
        <Fade in timeout={150} key={location.pathname}>
          <Box>
            <Outlet />
          </Box>
        </Fade>
      </Box>
    </Box>
  );
};

export default Layout;
